import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPatch } from "../api";
import { CardHover, CardThumb } from "../components/CardImage";
import { Skeleton, Spinner } from "../components/Loaders";
import { EmptyState, PageIntro } from "../components/Visual";
import { Icon } from "../components/Icons";
import type { SavedDeck, SavedDeckSummary } from "../types";

export function SavedDecks() {
  const [decks, setDecks] = useState<SavedDeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<SavedDeckSummary[]>("/api/decks?limit=100");
      setDecks(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRename = async (id: string, currentName: string) => {
    const name = window.prompt("Rename deck", currentName);
    if (!name || name.trim() === currentName) return;
    try {
      const updated = await apiPatch<SavedDeckSummary>(`/api/decks/${id}`, {
        name: name.trim(),
      });
      setDecks((xs) =>
        xs ? xs.map((d) => (d.id === id ? { ...d, ...updated } : d)) : xs,
      );
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onTogglePublish = async (deck: SavedDeckSummary) => {
    const next = !deck.published;
    const ok = next
      ? window.confirm(
          "Publish this deck to the community gallery? Other users can copy, analyze, and build from it.",
        )
      : window.confirm("Remove this deck from the community gallery?");
    if (!ok) return;
    try {
      const updated = await apiPatch<SavedDeckSummary>(`/api/decks/${deck.id}`, {
        published: next,
      });
      setDecks((xs) =>
        xs ? xs.map((d) => (d.id === deck.id ? { ...d, ...updated } : d)) : xs,
      );
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm("Delete this saved deck? This cannot be undone.")) return;
    try {
      await apiDelete(`/api/decks/${id}`);
      setDecks((xs) => (xs ? xs.filter((d) => d.id !== id) : xs));
    } catch (e) {
      alert((e as Error).message);
    }
  };

  return (
    <main className="page saved-page">
      <PageIntro
        eyebrow="Saved decks"
        icon="archive"
        title={<>Your personal <span className="grad">deck vault.</span></>}
        body="Every deck you save from the builder lands here with its rationale, full decklist, and Arena import text."
        actions={
          <>
            <Link to="/community" className="topbar-btn"><Icon name="grid" size={16} />Community decks</Link>
            <Link to="/build" className="topbar-btn primary"><Icon name="wand" size={16} />Build a new deck</Link>
          </>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="deck-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="deck-card" key={i}>
              <Skeleton variant="title" width="70%" style={{ marginBottom: 10 }} />
              <Skeleton variant="text" width="50%" style={{ marginBottom: 6 }} />
              <Skeleton variant="text" width="40%" />
            </div>
          ))}
        </div>
      ) : decks && decks.length === 0 ? (
        <EmptyState
          icon="cards"
          title="No decks saved yet"
          body="Generate a deck and click Save deck on the result to keep it here for later."
          action={<Link to="/build" className="topbar-btn primary">Open the builder</Link>}
        />
      ) : (
        <div className="deck-grid">
          {(decks || []).map((d) => (
            <DeckCard
              key={d.id}
              deck={d}
              onRename={() => onRename(d.id, d.name)}
              onDelete={() => onDelete(d.id)}
              onTogglePublish={() => onTogglePublish(d)}
            />
          ))}
        </div>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------

function DeckCard({
  deck,
  onRename,
  onDelete,
  onTogglePublish,
}: {
  deck: SavedDeckSummary;
  onRename: () => void;
  onDelete: () => void;
  onTogglePublish: () => void;
}) {
  const navigate = useNavigate();
  const colors = (deck.theme?.colors || []).join("") || "-";
  const main = deck.totals?.mainboard ?? 0;
  const side = deck.totals?.sideboard ?? 0;
  const updated = deck.updatedAt
    ? new Date(deck.updatedAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";
  return (
    <article
      className="deck-card deck-card--clickable"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/decks/${encodeURIComponent(deck.id)}`)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(`/decks/${encodeURIComponent(deck.id)}`);
        }
      }}
    >
      <DeckCardArt deck={deck} />
      <header className="deck-card-head">
        <h3>{deck.name}</h3>
        <div className="deck-card-pills">
          {deck.published && <span className="deck-meta-pill public">Public</span>}
          <span className="deck-meta-pill">{deck.format || "—"}</span>
        </div>
      </header>
      <div className="deck-card-row">
        <span className="muted">{deck.theme?.archetype || "No archetype"}</span>
        <span className="colors">{colors}</span>
      </div>
      <div className="deck-card-row">
        <span className="pill">Main {main}</span>
        {side > 0 && <span className="pill">Side {side}</span>}
        <span className="muted" style={{ marginLeft: "auto", fontSize: "0.8rem" }}>
          {updated}
        </span>
      </div>
      {deck.query && (
        <p className="deck-card-query muted">{deck.query}</p>
      )}
      <div className="deck-card-actions">
        <button className="secondary small" onClick={(event) => { event.stopPropagation(); onRename(); }}><Icon name="wand" size={14} />Rename</button>
        <button className="secondary small" onClick={(event) => { event.stopPropagation(); onTogglePublish(); }}>
          <Icon name="cloud" size={14} />
          {deck.published ? "Unpublish" : "Publish"}
        </button>
        <button className="danger small" onClick={(event) => { event.stopPropagation(); onDelete(); }}>Delete</button>
      </div>
    </article>
  );
}

export function DeckCardArt({
  deck,
  card,
  fit = "cover",
}: {
  deck?: SavedDeckSummary | SavedDeck | null;
  card?: { name: string; image_url?: string | null; type_line?: string | null } | null;
  fit?: "cover" | "contain";
}) {
  const cover =
    card ||
    ("coverCard" in (deck || {})
      ? (deck as SavedDeckSummary | null)?.coverCard
      : null);
  const colors = (deck?.theme?.colors || []).join(" ").toLowerCase() || "colorless";
  if (cover?.image_url) {
    return (
      <div className="deck-card-art" aria-hidden="true">
        <img
          src={cover.image_url}
          alt=""
          loading="lazy"
          draggable={false}
          className={fit === "contain" ? "art-fit-contain" : undefined}
        />
      </div>
    );
  }
  return (
    <div className={`deck-card-art deck-card-art--fallback ${colors}`} aria-hidden="true">
      <span>{(deck?.name || cover?.name || "Deck").slice(0, 1)}</span>
    </div>
  );
}

export function firstDeckCardArt(deck?: SavedDeck["deck"]) {
  const sections: (keyof NonNullable<SavedDeck["deck"]>)[] = [
    "commander",
    "companion",
    "mainboard",
    "sideboard",
  ];
  for (const section of sections) {
    const entry = (deck?.[section] || []).find((e) => e.card?.image_url);
    if (entry) {
      return {
        name: entry.card?.name || entry.name,
        image_url: entry.card?.image_url,
        type_line: entry.card?.type_line,
      };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------

function SavedDeckView({ id }: { id: string }) {
  const [deck, setDeck] = useState<SavedDeck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDeck(null);
    setError(null);
    apiGet<SavedDeck>(`/api/decks/${id}`)
      .then((d) => {
        if (!cancelled) setDeck(d);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copyArena = async () => {
    if (!deck?.arena_text) return;
    try {
      await navigator.clipboard.writeText(deck.arena_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="saved-deck-view rationale-block deck-detail-shell">
      <div className="deck-detail-hero">
        <DeckCardArt deck={deck} card={firstDeckCardArt(deck?.deck)} />
        <div>
          <div className="deck-detail-kicker">Saved deck</div>
          <h3>
            {deck?.name ? deck.name : <Skeleton variant="title" width={180} />}
          </h3>
          {deck && (
            <div className="deck-public-meta">
              <span>{deck.format || "standard"}</span>
              <span>{deck.theme?.archetype || "No archetype"}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!deck && !error && (
        <>
          <Skeleton variant="text" width="80%" style={{ marginBottom: 8 }} />
          <Skeleton variant="block" height={120} />
        </>
      )}

      {deck && (
        <>
          {deck.rationale && (
            <div className="rationale-block" style={{ marginTop: 0 }}>
              <h4>Rationale</h4>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, margin: 0 }}>
                {deck.rationale}
              </p>
            </div>
          )}

          <div className="deck-view">
            <h3>Decklist</h3>
            <DeckSectionsReadonly deck={deck.deck} />
          </div>

          {deck.arena_text && (
            <div className="arena-block">
              <div className="arena-header">
                <h3>MTG Arena import text</h3>
                <button className="secondary" onClick={copyArena}>
                  {copied ? (
                    "Copied"
                  ) : (
                    <>
                      <Spinner
                        className="button-spinner"
                        style={{ display: "none" }}
                      />
                      <Icon name="clipboard" size={16} />
                      Copy to clipboard
                    </>
                  )}
                </button>
              </div>
              <pre className="arena-text" spellCheck={false}>
                {deck.arena_text}
              </pre>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function SavedDeckDetailsPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <main className="page saved-page">
        <div className="error-banner">Deck not found.</div>
      </main>
    );
  }
  return (
    <main className="page saved-page">
      <PageIntro
        eyebrow="Saved decks"
        icon="cards"
        title={<>Deck <span className="grad">details</span></>}
        body="Full deck rationale, complete decklist, and Arena import text."
        actions={<Link to="/decks" className="topbar-btn">Back to saved decks</Link>}
      />
      <SavedDeckView id={id} />
    </main>
  );
}

export function DeckSectionsReadonly({ deck }: { deck?: SavedDeck["deck"] }) {
  const [viewMode, setViewMode] = useState<"list" | "gallery">("list");
  if (!deck) return null;
  const order: (keyof NonNullable<SavedDeck["deck"]>)[] = [
    "mainboard",
    "commander",
    "companion",
    "sideboard",
  ];
  return (
    <>
      <div className="deck-view-toolbar">
        <div
          className="deck-view-modes"
          role="tablist"
          aria-label="Deck view mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "list"}
            className={viewMode === "list" ? "active" : undefined}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "gallery"}
            className={viewMode === "gallery" ? "active" : undefined}
            onClick={() => setViewMode("gallery")}
          >
            Images
          </button>
        </div>
      </div>
      {order.map((key) => {
        const entries = (deck?.[key] || []).filter((e) => e.count > 0);
        if (!entries.length) return null;
        const total = entries.reduce((acc, e) => acc + e.count, 0);
        const title = `${key.charAt(0).toUpperCase() + key.slice(1)} (${total})`;
        if (viewMode === "gallery") {
          return (
            <div key={key} className="deck-section">
              <h4>{title}</h4>
              <div className="deck-gallery">
                {entries.map((e, i) => (
                  <div className="deck-gallery-item" key={`${e.name}-${i}`}>
                    <CardThumb
                      card={{
                        name: e.name,
                        image_url: e.card?.image_url,
                        type_line: e.card?.type_line,
                      }}
                      size="md"
                    />
                    <div className="deck-gallery-caption">
                      <span className="qty">{e.count}×</span>
                      <span className="name">{e.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="deck-section">
            <h4>{title}</h4>
            <ul>
              {entries.map((e, i) => {
                const meta = e.card?.mana_cost || e.card?.type_line || "";
                return (
                  <li key={`${e.name}-${i}`}>
                    <span className="qty">{e.count}</span>
                    <CardHover
                      card={{
                        name: e.name,
                        image_url: e.card?.image_url,
                        type_line: e.card?.type_line,
                        mana_cost: e.card?.mana_cost,
                      }}
                      className="name"
                    >
                      {e.name}
                    </CardHover>
                    <span className="meta">{meta}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </>
  );
}
